import React, { createContext, useContext, useState, useCallback } from 'react'
import { Snackbar, Alert, Slide, SlideProps } from '@mui/material'

/**
 * Toast Notification Context for Simple Deletion Feedback
 * Phase 35: Simplified Document Deletion System
 *
 * Provides simple toast notifications to replace complex WebSocket progress tracking
 */

type ToastSeverity = 'success' | 'error' | 'info' | 'warning'

interface Toast {
  id: string
  message: string
  type: ToastSeverity
  duration: number
  timestamp: number
}

interface ToastContextValue {
  // State
  toasts: Toast[]
  activeToast: Toast | null

  // Actions
  showToast: (message: string, type?: ToastSeverity, duration?: number) => string
  showSuccess: (message: string, duration?: number) => string
  showError: (message: string, duration?: number) => string
  showInfo: (message: string, duration?: number) => string
  showWarning: (message: string, duration?: number) => string
  removeToast: (id: string) => void
  clearAllToasts: () => void

  // Utilities
  hasToasts: boolean
}

const ToastContext = createContext<ToastContextValue | null>(null)

// Toast types with Material-UI Alert severity mapping
const TOAST_TYPES: Record<string, ToastSeverity> = {
  SUCCESS: 'success',
  ERROR: 'error',
  INFO: 'info',
  WARNING: 'warning'
}

// Slide transition for smooth appearance
function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="up" />
}

interface ToastProviderProps {
  children: React.ReactNode
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([])

  // Generate unique IDs for toasts
  const generateId = useCallback((): string => {
    return `toast_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }, [])

  // Add a new toast to the queue
  const addToast = useCallback((message: string, type: ToastSeverity = TOAST_TYPES.INFO, duration: number = 4000): string => {
    const id = generateId()
    const newToast: Toast = {
      id,
      message,
      type,
      duration,
      timestamp: Date.now()
    }

    setToasts(prevToasts => [...prevToasts, newToast])

    // Auto-remove toast after duration
    if (duration > 0) {
      setTimeout(() => {
        removeToast(id)
      }, duration)
    }

    return id
  }, [generateId])

  // Remove a specific toast
  const removeToast = useCallback((id: string) => {
    setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id))
  }, [])

  // Clear all toasts
  const clearAllToasts = useCallback(() => {
    setToasts([])
  }, [])

  // Convenience methods for different toast types
  const showSuccess = useCallback((message: string, duration: number = 4000): string => {
    return addToast(message, TOAST_TYPES.SUCCESS, duration)
  }, [addToast])

  const showError = useCallback((message: string, duration: number = 6000): string => {
    return addToast(message, TOAST_TYPES.ERROR, duration)
  }, [addToast])

  const showInfo = useCallback((message: string, duration: number = 4000): string => {
    return addToast(message, TOAST_TYPES.INFO, duration)
  }, [addToast])

  const showWarning = useCallback((message: string, duration: number = 5000): string => {
    return addToast(message, TOAST_TYPES.WARNING, duration)
  }, [addToast])

  // Get the currently active toast (only show one at a time for simplicity)
  const activeToast = toasts.length > 0 ? toasts[0] : null

  const value: ToastContextValue = {
    // State
    toasts,
    activeToast,

    // Actions
    showToast: addToast,
    showSuccess,
    showError,
    showInfo,
    showWarning,
    removeToast,
    clearAllToasts,

    // Utilities
    hasToasts: toasts.length > 0
  }

  return (
    <ToastContext.Provider value={value}>
      {children}

      {/* Toast Display - Show only the first toast for simplicity */}
      {activeToast && (
        <Snackbar
          open={true}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
          TransitionComponent={SlideTransition}
          onClose={() => removeToast(activeToast.id)}
          sx={{
            // Ensure toast appears above other components
            zIndex: theme => theme.zIndex.snackbar + 1,
            // Offset from bottom-right corner
            bottom: 24,
            right: 24
          }}
        >
          <Alert
            severity={activeToast.type}
            onClose={() => removeToast(activeToast.id)}
            variant="filled"
            sx={{
              // Ensure good visibility and readability
              minWidth: 300,
              maxWidth: 500,
              '& .MuiAlert-message': {
                fontSize: '0.875rem',
                lineHeight: 1.4
              }
            }}
          >
            {activeToast.message}
          </Alert>
        </Snackbar>
      )}
    </ToastContext.Provider>
  )
}

// Hook to use the toast context
export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}

// Export toast types for convenience
export { TOAST_TYPES }
export default ToastContext